import { useMemo, useState } from 'react';
import {
  Grid,
  GridCustomCellProps,
  GridColumn,
  GridNoRecords,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { enMessages } from '../../../../../../messages/en-US';
import { useLocalization } from '@progress/kendo-react-intl';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { formatSmartDate } from '../../../../../Shared/Utilities/DateFormattingUtils';
import { SvgIcon } from '@progress/kendo-react-common';
import { infoCircleIcon } from '@progress/kendo-svg-icons';
import useIdentityStore from '../../../../../../Zustand/identityStore';
import EntityNotesButton from '../../Notes/EntityNotesButton';

interface Props {
  assetData?: any;
}

const isGreasePumpActive = (pump: any) =>
  pump?.status === 'Active' || (pump?.status === undefined && pump?.isEnabled !== false);

const LastActivityCell = (props: GridCustomCellProps) => {
  const localizationService = useLocalization();
  const { displayText: lastSeenText, tooltipText } = formatSmartDate(
    props.dataItem.lastActivity || props.dataItem.lastGreaseEvent,
    localizationService,
    enMessages
  );

  return (
    <td>
      <Tooltip
        anchorElement="target"
        position="bottom"
        parentTitle={true}
        content={() => tooltipText}
      >
        <span style={{ cursor: 'pointer' }} title=" ">
          {lastSeenText}
        </span>
      </Tooltip>
    </td>
  );
};

const NotesCell = (props: GridCustomCellProps) => {
  const pump = props.dataItem;
  // GreasePump notes are keyed by the pump's deviceId (not its internal id). Without a device
  // there's nothing to attach a note to, so hide the button.
  if (!pump?.deviceId) return <td />;
  return (
    <td>
      <EntityNotesButton
        entityType="GreasePump"
        entityId={String(pump.deviceId)}
        entityName={pump.name || pump.serialNumber}
        entitySerial={pump.serialNumber}
      />
    </td>
  );
};

const InfoCell = (props: GridCustomCellProps) => {
  const isSuperAdmin = useIdentityStore((x) => x.user?.profile?.role) === 'SuperAdmin';
  const { dataItem } = props;

  return (
    <td style={{ textAlign: 'center' }}>
      <Tooltip
        anchorElement="target"
        position="left"
        parentTitle={true}
        content={() => (
          <div>
            <div>Channel: {dataItem.channelNumber ?? '-'}</div>
            {isSuperAdmin && <div>Timing Offset: {dataItem.timingOffsetSeconds ?? 0} secs</div>}
          </div>
        )}
      >
        <span title=" " style={{ cursor: 'pointer' }}>
          <SvgIcon icon={infoCircleIcon} size="medium" className="text-gray-500" />
        </span>
      </Tooltip>
    </td>
  );
};

const GreasePumpsTableComponent: React.FC<Props> = ({ assetData = {} }) => {
  const localizationService = useLocalization();

  const [sort, setSort] = useState<SortDescriptor[]>([]);

  const greasePumps = useMemo(() => {
    const rows = (assetData?.assetConfigurationDetails?.greasePumps ?? [])
      .filter(isGreasePumpActive)
      // Normalize lastActivity (the cell falls back to lastGreaseEvent) so sorting matches what's displayed
      .map((pump: any) => ({ ...pump, lastActivity: pump.lastActivity ?? pump.lastGreaseEvent }));
    return sort.length ? orderBy(rows, sort) : rows;
  }, [assetData, sort]);

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="text-lg font-bold mb-4">
        {localizationService.toLanguageString('custom.greasePumps', enMessages.custom.greasePumps)}
      </div>
      <Grid
        data={greasePumps}
        style={{ overflow: 'auto' }}
        scrollable="scrollable"
        sortable={{ mode: 'single' }}
        sort={sort}
        onSortChange={(event: GridSortChangeEvent) => setSort(event.sort)}
      >
          <GridNoRecords>
            {localizationService.toLanguageString('custom.noGreasePumpsConfigured', enMessages.custom.noGreasePumpsConfigured)}
          </GridNoRecords>
          <GridColumn
            field="name"
            title={localizationService.toLanguageString('custom.name', enMessages.custom.name)}
          />
          <GridColumn
            field="serialNumber"
            title={localizationService.toLanguageString('custom.device', enMessages.custom.device)}
          />
          <GridColumn
            field="lastActivity"
            title={localizationService.toLanguageString('custom.lastActivity', enMessages.custom.lastActivity)}
            cell={LastActivityCell}
          />
          <GridColumn
            title=" "
            width={65}
            cells={{ data: NotesCell }}
            sortable={false}
          />
          <GridColumn
            title=" "
            width={48}
            cell={InfoCell}
            sortable={false}
          />
      </Grid>
    </div>
  );
};

export default GreasePumpsTableComponent;
